import React from 'react';

import { CANVAS_SCALE, canvasDisplaySpecifics, receiptCanvasSize } from 'src/features/statistics/utils/digitalReceipt';
import { ReceiptFont } from 'src/features/statistics/utils/receiptFont';

import type { ReceiptRow } from 'src/features/statistics/types/receiptRow';

interface DigitalReceiptCanvasProps {
    /** Flat text, one entry per line. Used when there are no structured rows. */
    receiptTextLines?: string[];
    /**
     * The document's rows. Preferred over receiptTextLines: the flat text has had
     * the layout flattened out of it, so a double-width total is indistinguishable
     * from an ordinary line and a centred header sits flush left.
     */
    rows?: ReceiptRow[];
    /** Columns the document is laid out on, as the endpoint reports it. */
    charsPerLine?: number;
}

const DigitalReceiptCanvas = React.forwardRef<React.ElementRef<'canvas'>, DigitalReceiptCanvasProps>(
    ({ receiptTextLines, rows, charsPerLine }, ref) => {
        const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
        const [isLoadedFont, setIsLoadedFont] = React.useState(false);

        React.useEffect(() => {
            let cancelled = false;
            void ReceiptFont.ready().then(() => {
                if (!cancelled) setIsLoadedFont(true);
            });
            return () => {
                cancelled = true;
            };
        }, []);

        const drawing = { lines: receiptTextLines, rows, charsPerLine };
        const { width, height, structured } = receiptCanvasSize(drawing);

        React.useEffect(() => {
            if (!canvasRef.current || !isLoadedFont) {
                return;
            }
            canvasDisplaySpecifics(canvasRef.current, { lines: receiptTextLines, rows, charsPerLine });
        }, [receiptTextLines, rows, charsPerLine, isLoadedFont]);

        if (!structured && !receiptTextLines) return null;

        // The element carries the supersampled backing store while the style keeps it
        // on screen at its logical size, so the browser scales it DOWN rather than up.
        return (
            <canvas
                ref={(node) => {
                    canvasRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                width={width * CANVAS_SCALE}
                height={height * CANVAS_SCALE}
                style={{ width, height }}
                className="rounded-xl border p-2 shadow-sm"
            />
        );
    },
);
DigitalReceiptCanvas.displayName = 'DigitalReceiptCanvas';

export { DigitalReceiptCanvas };
